import { prop, propOr, view, lensPath } from 'ramda'
import { currentTotalToPercentage } from 'libs/math'

export const IMAGE_COUNT_LIMITS = {
  SOFT_WARNING: 50,
  SECOND_WARNING: 100,
  HARD_CAP: 150,
}

export const DISPLAY_LIMITS = {
  PREVIEW: 3,
  GRID: 5,
}

export const generateUploadId = (fileName) =>
  `${Date.now()}-${Math.random()}-${fileName}`

export const createTempImage = (file, uploadId, isFirstImage) => {
  return {
    uploadId,
    url: null,
    name: file.name,
    isLoading: true,
    percentage: 0,
    size: file.size,
    isSelected: isFirstImage,
  }
}

export const createUploadIdMap = (tempImages) =>
  tempImages.reduce((map, img) => {
    map[img.name] = img.uploadId
    return map
  }, {})

export const validateImageCount = (
  currentCount,
  newFilesCount,
  formatMessage,
) => {
  const newCount = currentCount + newFilesCount

  if (newCount > IMAGE_COUNT_LIMITS.HARD_CAP) {
    return {
      isValid: false,
      shouldBlock: true,
      error: {
        message: formatMessage({
          id: 'compose.dialog.attachments.error.limitExceeded',
        }),
      },
    }
  }

  const warnings = []

  if (
    currentCount < IMAGE_COUNT_LIMITS.SECOND_WARNING &&
    newCount >= IMAGE_COUNT_LIMITS.SECOND_WARNING &&
    newCount <= IMAGE_COUNT_LIMITS.HARD_CAP
  ) {
    warnings.push({
      type: 'warning',
      message: formatMessage({
        id: 'compose.dialog.attachments.warning.highImageCount',
      }),
    })
  }

  if (
    currentCount < IMAGE_COUNT_LIMITS.SOFT_WARNING &&
    newCount >= IMAGE_COUNT_LIMITS.SOFT_WARNING &&
    newCount < IMAGE_COUNT_LIMITS.SECOND_WARNING
  ) {
    warnings.push({
      type: 'warning',
      message: formatMessage({
        id: 'compose.dialog.attachments.warning.mediumImageCount',
      }),
    })
  }

  return { isValid: true, shouldBlock: false, warnings }
}

export const replaceTempImage = (images, uploadedImage) => {
  const uploadId = uploadedImage.uploadId
  if (!uploadId) return images

  const foundIndex = images.findIndex(
    (img) => prop('uploadId', img) === uploadId,
  )

  if (foundIndex === -1) {
    return [...images, { ...uploadedImage, isSelected: images.length === 0 }]
  }

  const existingImg = images[foundIndex]

  const updated = [...images]
  updated[foundIndex] = {
    ...uploadedImage,
    isSelected: propOr(false, 'isSelected', existingImg) || images.length === 0,
    isLoading: false,
    percentage: 100,
  }

  return updated
}

export const cleanupFailedUploads = (images) => {
  const cleaned = images.filter((img) => {
    if (prop('isLoading', img) && !prop('url', img)) {
      return false
    }
    return true
  })
  return cleaned
}

export const createProgressHandlerFactory = (setValue) => {
  return (uploadId) => {
    return ({ loaded, total }) => {
      const percentage = currentTotalToPercentage(loaded, total)

      setValue((prevImages) =>
        prevImages.map((img) =>
          prop('uploadId', img) === uploadId ? { ...img, percentage } : img,
        ),
      )
    }
  }
}

export const getFilesFromEvent = (event) => {
  const files = view(lensPath(['target', 'files']))(event)
  return files ? Array.from(files) : []
}

export const isImageLoading = (image) => propOr(false, 'isLoading', image)

export const getNonLoadingImagesCount = (images) =>
  images.filter((img) => !isImageLoading(img)).length

export const shouldShowMoreButton = (images, index, isPreview, displayLimit) =>
  !isPreview &&
  !isImageLoading(images[index]) &&
  getNonLoadingImagesCount(images) > displayLimit &&
  index === displayLimit - 1 &&
  images.slice(0, index).every((img) => !isImageLoading(img))

export const shouldShowImage = (index, isPreview, displayLimit) => {
  const limit = isPreview ? DISPLAY_LIMITS.PREVIEW : displayLimit
  return index < limit
}

export const getRemainingImagesCount = (images, displayLimit) =>
  Math.max(0, getNonLoadingImagesCount(images) - displayLimit + 1)
